import classNames from 'clsx';
import React, { memo, ReactElement, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { Button } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { ESplashScreenMod, ISplashScreenProps } from 'reactApp/ui/Mobile/SplashScreen/SplashScreen.types';
import { sendDwh } from 'reactApp/utils/ga';
import { noopVoid } from 'reactApp/utils/helpers';

import styles from './SplashScreen.css';

export const SplashScreen = memo(({ contentHook, productId = '', onClose }: ISplashScreenProps): ReactElement => {
    const storage = useSelector(getCurrentStorage);

    const {
        id,
        title,
        description,
        buttonText,
        footerText,
        img,
        mod = ESplashScreenMod.blue,
        onClick = noopVoid,
    } = contentHook({ productId, onClose });

    const sendScreenDwh = useCallback(
        (action) => {
            sendDwh({
                eventCategory: 'splashscreen-alert',
                action,
                dwhData: {
                    name: id,
                    source: storage,
                    button_name: buttonText,
                },
                sendImmediately: true,
            });
        },
        [id, storage]
    );

    useEffect(() => {
        sendScreenDwh('show');
    }, []);

    const onCloseCallback = useCallback(() => {
        sendScreenDwh('close');
        onClose();
    }, [onClose, sendScreenDwh]);

    const onClickCallback = useCallback(() => {
        sendScreenDwh('click');
        onClick();
    }, [onClick, sendScreenDwh]);

    return (
        <MobileDialog id={id} onClose={onCloseCallback} disableScrolling mod="base" topmost fullHeight className={styles[`root_${mod}`]}>
            <div className={classNames(styles.root, styles[`root_${mod}`])}>
                <img src={img} className={styles.image} />
                <div className={styles.content}>
                    <div className={styles.title}>{title}</div>
                    <div className={styles.description}>{description}</div>
                    <Button className={styles.button} theme="vk" primary onClick={onClickCallback}>
                        {buttonText}
                    </Button>
                    {footerText && <div className={styles.footer}>{footerText}</div>}
                </div>
            </div>
        </MobileDialog>
    );
});

SplashScreen.displayName = 'SplashScreen';
